import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sidebar } from '@/components/layout/Sidebar';
import { MobileTopBar } from '@/components/layout/MobileTopBar';
import { Topbar } from '@/components/layout/Topbar';
import {
  BreadcrumbBar,
  BreadcrumbOverrideProvider,
} from '@/components/layout/BreadcrumbBar';
import { TenantProvider } from '@/contexts/TenantContext';
import { ActiveOrgIdSync } from '@/lib/api/use-active-org-id-sync';
import { CommandPalette } from '@/lib/command-palette/CommandPalette';
import { seedCommandActions } from '@/lib/command-palette/seed';
import { ShortcutsProvider } from '@/lib/shortcuts/ShortcutsProvider';
import { HelpSheet } from '@/lib/shortcuts/HelpSheet';
import { seedShortcuts } from '@/lib/shortcuts/seed';
import { UserPreferencesProvider } from '@/lib/user-preferences/UserPreferencesProvider';
import { FeedbackButton } from '@/components/feedback/FeedbackButton';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';
import { ServerErrorBridge } from '@/components/feedback/ServerErrorBridge';
import { ActiveOrgErrorBridge } from '@/components/feedback/ActiveOrgErrorBridge';
import { DynamicFavicon } from '@/components/branding/DynamicFavicon';
import { DocumentTitle } from '@/components/branding/DocumentTitle';
import { ConnectionStatusBanner } from '@/components/system/ConnectionStatusBanner';
import { PwaUpdateBanner } from '@/components/system/PwaUpdateBanner';
import { TimeTravelBanner } from '@/components/time-travel/TimeTravelBanner';
import { DeprecationBanner } from '@/components/system/DeprecationBanner';

/**
 * AppShell — canonical SustEvo App Shell.
 *
 * Per `sustevo-handbook/brand/APP_SHELL.md` § 2 the grid is:
 *
 *     md:grid-cols-[15rem_1fr]
 *     ─────────────────────────────────────────────────
 *     | Sidebar    | Content                          |
 *     | (sticky)   | (full width, no persistent topbar)|
 *     ─────────────────────────────────────────────────
 *
 * On `<md` the grid collapses to a single column, the sidebar becomes
 * a fixed slide-in panel toggled by `<MobileTopBar>`'s hamburger, and
 * a backdrop covers the content area when the panel is open. Auto-close
 * on route change keeps the panel from sticking after navigation.
 *
 * BreadcrumbBar lives at the top of the content area (not in chrome)
 * per APP_SHELL.md § 9 "What's explicitly NOT in the shell" — it's a
 * content concern, rendered inside `<main>`.
 *
 * Globals mounted here (need `useNavigate`):
 *   - `CommandPalette` — Cmd+K / Ctrl+K palette over the whole app.
 *   - `ShortcutsProvider` — global keydown listener.
 *   - `HelpSheet` — `?` help sheet listing all active shortcuts.
 */
export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Element that opened the mobile drawer — focus returns here on close
  // per MOBILE.md § 9.1 + HARMONIZATION.md § 14.
  const triggerOnOpenRef = useRef<HTMLElement | null>(null);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    seedCommandActions(t, navigate);
    seedShortcuts(t, navigate);
  }, [t, navigate]);

  // Auto-close the mobile slide-in panel after navigation so it doesn't
  // stick after a route change (APP_SHELL.md § 8).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // Mobile drawer focus contract (MOBILE.md § 9.1):
  //   - On open: stash the focused element (= the hamburger toggle), then
  //     move focus to the drawer landmark so screen readers announce the
  //     navigation context.
  //   - Escape dismisses the drawer.
  //   - On close: restore focus to the stashed trigger so keyboard users
  //     don't lose their place.
  //
  // The `inert` attribute on `<main>` (set below) keeps the background
  // content out of the tab order while the drawer is open — Tab cycles
  // drawer → top-bar → drawer without falling into the article body.
  useEffect(() => {
    if (!mobileNavOpen) return;
    triggerOnOpenRef.current = document.activeElement as HTMLElement | null;
    const drawer = document.getElementById('app-shell-sidebar');
    drawer?.focus({ preventScroll: true });

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMobileNav();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen, closeMobileNav]);

  // Separate effect — only fires on the transition from open → closed.
  // Restoring focus inside the same `mobileNavOpen` effect runs the
  // cleanup on every dependency change, which would steal focus even
  // when the drawer was never open (initial mount, route change).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !mobileNavOpen) {
      triggerOnOpenRef.current?.focus({ preventScroll: true });
      triggerOnOpenRef.current = null;
    }
    wasOpenRef.current = mobileNavOpen;
  }, [mobileNavOpen]);

  return (
    <UserPreferencesProvider>
      <ShortcutsProvider>
        <TenantProvider>
          {/*
            W102-F1-S2 — keep the SPA's active-org id in sync with the
            user's memberships, and register the getter that `apiFetch`
            uses to stamp the X-Org-Id header. Renders nothing; must
            sit inside <TenantProvider> so the hook can read+write
            activePartnerId.
          */}
          <ActiveOrgIdSync />
          <BreadcrumbOverrideProvider>
            {/*
              Brand gradient hairline — always-on-top 3px strip carrying
              the SustEvo blue→green gradient. Persistent identity marker
              that doesn't fight with data. Handbook BRAND.md § 3.4
              reserves the gradient for marketing/auth/brand-expression
              surfaces — a hairline above the chrome qualifies. Matches
              PM canonical (`Sustevo-Core/project-management-tool/app/layout.tsx`
              lines 90-95).
            */}
            <div
              aria-hidden="true"
              data-testid="app-shell-brand-hairline"
              className="pointer-events-none fixed inset-x-0 top-0 z-[9999] h-[3px]"
              style={{ background: 'var(--gradient-brand)' }}
            />
            <div
              // BRAND.md § 3.6 token discipline — chrome reads the canonical
              // ink scale directly (explicit light/dark pair) instead of the
              // shadcn `bg-background` / `text-foreground` aliases, so the
              // same source string ships the same colour across every SustEvo
              // app rather than silently bridging to a per-app override.
              className="grid min-h-screen grid-cols-1 bg-[var(--color-ink-0)] text-[var(--color-ink-900)] dark:bg-[var(--color-ink-900)] dark:text-[var(--color-ink-100)] md:grid-cols-[15rem_1fr]"
              data-testid="app-shell-grid"
            >
              {/* Sidebar is itself the <aside> landmark with slide-in
                  transform + sticky classes — single landmark, matches
                  PM canonical (no outer wrapper). */}
              <Sidebar
                open={mobileNavOpen}
                onClose={closeMobileNav}
              />

              {/* Backdrop for the mobile slide-in panel — pointer-only
                  convenience per APP_SHELL.md § 8.6 + HARMONIZATION.md § 6.1
                  (ink-900 @ 40% opacity). Keyboard users close via Escape
                  (wired in the AppShell focus effect above) or the hamburger
                  toggle, which is reachable because <main> is the only inert
                  element. */}
              {mobileNavOpen && (
                // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop is pointer-only; keyboard close is Escape or the <MobileTopBar> toggle.
                <div
                  role="presentation"
                  onClick={closeMobileNav}
                  data-testid="mobile-drawer-backdrop"
                  data-legacy-testid="app-shell-backdrop"
                  className="fixed inset-0 z-30 bg-[var(--color-ink-900)]/40 md:hidden motion-reduce:transition-none"
                />
              )}

              {/* Content column. */}
              <div className="min-w-0 flex flex-col">
                <MobileTopBar
                  open={mobileNavOpen}
                  onToggle={() => setMobileNavOpen((o) => !o)}
                />
                {/* Persistent desktop top bar — APP_SHELL.md § 8.
                    Hidden on mobile (the `<MobileTopBar />` above
                    handles that breakpoint). Right cluster: bell +
                    user menu. */}
                <Topbar />
                {/* #1550 — Global time-travel banner. Renders only when
                    a platform admin has flipped the override on; null
                    otherwise. Visible to every authenticated user so
                    nobody is surprised by a simulated date. */}
                <TimeTravelBanner />
                {/* #1517 — Legacy-platform sunset notice. Config-gated
                    (VITE_SUNSET_BANNER_ENABLED), default-OFF; renders null
                    until ops activates the announcement. Dismissible +
                    persisted per sunset date so it doesn't nag every load. */}
                <DeprecationBanner />
                <main
                  className="flex-1 overflow-y-auto px-4 py-6 lg:px-8 lg:py-8"
                  // MOBILE.md § 9.1: the article body is inert while the
                  // drawer is open so screen readers / keyboard tab cycles
                  // skip it. The MobileTopBar (where the close-X lives)
                  // stays reachable on purpose — it sits OUTSIDE <main>.
                  inert={mobileNavOpen ? true : undefined}
                  data-mobile-inert={mobileNavOpen ? 'true' : undefined}
                >
                  {/* Breadcrumb trail at the top of content per
                      APP_SHELL.md § 9 — content concern, not chrome. */}
                  <BreadcrumbBar />
                  <Suspense fallback={<RouteFallback />}>
                    <Outlet />
                  </Suspense>
                </main>
              </div>

              <CommandPalette />
              <HelpSheet />
              <FeedbackButton />
              <FeedbackModal />
              <ServerErrorBridge />
              {/* W102-F1-S3 — surface resolveActiveOrg 403 codes as
                  helpful toasts. Mounted after ServerErrorBridge so
                  the two reporters register cleanly in declaration
                  order. */}
              <ActiveOrgErrorBridge />
              {/* Mandatory overlays per sustevo-handbook/brand/APP_SHELL.md
                  § 10. ConnectionStatusBanner renders nothing while
                  online; PwaUpdateBanner renders nothing until the SW
                  signals a new build. Both add zero visual weight in
                  the normal case. */}
              <ConnectionStatusBanner />
              <PwaUpdateBanner />
              {/* W5-F1-S3 / S4 — branding-driven side effects.
                  DynamicFavicon swaps the favicon to the partner's URL.
                  DocumentTitle keeps the browser tab title in sync with
                  the partner product_name + active route. */}
              <DynamicFavicon />
              <DocumentTitle />
            </div>
          </BreadcrumbOverrideProvider>
        </TenantProvider>
      </ShortcutsProvider>
    </UserPreferencesProvider>
  );
}

/**
 * Suspense fallback for lazy-loaded route children. Matches the same
 * skeleton shape used by inline route Suspense boundaries (kept in
 * router.tsx where a custom fallback is needed).
 */
function RouteFallback(): JSX.Element {
  return (
    <div
      className="space-y-2 rounded-md border border-[var(--color-ink-200)] p-4 dark:border-[var(--color-ink-700)]"
      role="status"
      aria-busy="true"
    >
      <div className="h-8 w-1/3 animate-pulse rounded bg-[var(--color-ink-50)] dark:bg-[var(--color-ink-700)]" />
      <div className="h-32 w-full animate-pulse rounded bg-[var(--color-ink-50)] dark:bg-[var(--color-ink-700)]" />
    </div>
  );
}
